import React, { useContext, useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import OrgContext from '../context/OrgContext';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { BiQr } from 'react-icons/bi'; // Import QR Code icon from React Icons
import jsPDF from 'jspdf';
import QRCode from 'qrcode';


const OrgManager = () => {
    const { user, refreshUser } = useContext(OrgContext);
    const [filteredOrgs, setFilteredOrgs] = useState([]);
    const [locations, setLocations] = useState([]); // Initialize as an empty array
    const [searchQuery, setSearchQuery] = useState('');
    const [orgDialogOpen, setOrgDialogOpen] = useState(false);
    const [locationDialogOpen, setLocationDialogOpen] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [expandedOrgs, setExpandedOrgs] = useState({});
    const [newLocation, setNewLocation] = useState({ name: '', contact: '', terminals: 0 });
    const [selectedLocation, setSelectedLocation] = useState(null);
    const token = sessionStorage.getItem('token');

    // Fetch user organizations on mount
    useEffect(() => {
        if (user && user.organizationIDs) {
            setFilteredOrgs(user.organizationIDs);
        }
        fetchLocations(); // Fetch all locations for the account
    }, [user]);

    const resetLocationDialog = () => {
        setSelectedLocation(null);   // Clear selected location to indicate a new entry
        setNewLocation({ name: '', contact: '', terminals: 0 }); // Reset newLocation to default values
        setLocationDialogOpen(false); // Close the dialog
    };

    // Fetch all locations for the account
    const fetchLocations = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/locations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (Array.isArray(response.data)) {
                setLocations(response.data); // Only set locations if the data is an array
            } else {
                console.error('Unexpected response format:', response.data);
                toast.error('Failed to fetch locations: unexpected response format');
            }
        } catch (error) {
            console.error('Error fetching locations:', error);
            toast.error('Failed to fetch locations');
        }
    };

    // Filter locations based on orgID
    const getLocationsForOrg = (orgId) => {
        return locations.filter((location) => location.orgID === orgId);
    };

    // Search filter for organizations
    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (user && user.organizationIDs) {
            setFilteredOrgs(
                user.organizationIDs.filter(org =>
                    org.orgName.toLowerCase().includes(query.toLowerCase())
                )
            );
        }
    };

    const toggleExpand = (orgId) => {
        setExpandedOrgs((prev) => ({
            ...prev,
            [orgId]: !prev[orgId],
        }));
    };

    const openOrgDialog = (org = null) => {
        setSelectedOrg(org || { orgName: '', orgNr: '', valute: '' });
        setOrgDialogOpen(true);
    };

    const handleCreateOrUpdateOrg = async () => {
        const payload = {
            orgName: selectedOrg.orgName,
            orgNr: selectedOrg.orgNr,
            valute: selectedOrg.valute
        };
        const url = selectedOrg._id
            ? `${process.env.REACT_APP_SRV}/orgs/${selectedOrg._id}`
            : `${process.env.REACT_APP_SRV}/orgs`;
        const method = selectedOrg._id ? 'put' : 'post';

        try {
            const response = await axios[method](url, payload, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(response.data.message || `Organization ${selectedOrg._id ? 'updated' : 'created'} successfully`);
            await refreshUser();
            setOrgDialogOpen(false);
        } catch (error) {
            console.error('Error creating/updating organization:', error);
            toast.error(`Error ${selectedOrg._id ? 'updating' : 'creating'} organization`);
        }
    };

    const handleDeleteOrg = async (orgID) => {
        try {
            await axios.delete(`${process.env.REACT_APP_SRV}/orgs/${orgID}`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Organization deleted successfully');
            await refreshUser();
        } catch (error) {
            console.error('Error deleting organization:', error);
            toast.error('Error deleting organization');
        }
    };

    const handleAddLocation = (org) => {
        setSelectedOrg(org);
        setSelectedLocation(null); // Clear selected location to indicate a new entry
        setNewLocation({ name: '', contact: '', terminals: 0 });
        setLocationDialogOpen(true);
    };

    const handleSaveLocation = async () => {
        const method = selectedLocation?._id ? 'put' : 'post';
        const url = selectedLocation?._id
            ? `${process.env.REACT_APP_SRV}/locations/${selectedLocation._id}`
            : `${process.env.REACT_APP_SRV}/locations`;
    
        try {
            const response = await axios[method](url, newLocation, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: `${selectedOrg._id}`,
                },
            });
    
            toast.success(`${selectedLocation?._id ? 'Location updated' : 'Location created'} successfully`);
    
            // Update locations state locally without refetching
            if (selectedLocation?._id) {
                // Update an existing location
                setLocations(prevLocations =>
                    prevLocations.map(loc => loc._id === selectedLocation._id ? response.data : loc)
                );
            } else {
                // Add a new location
                setLocations(prevLocations => [...prevLocations, response.data]);
            }
    
            setLocationDialogOpen(false); // Close the dialog
        } catch (error) {
            console.error(`Error ${selectedLocation?._id ? 'updating' : 'creating'} location:`, error);
            toast.error(`Failed to ${selectedLocation?._id ? 'update' : 'create'} location`);
        }
    };
    

    const handleDeleteLocation = async (locationId, orgId) => {
        try {
            await axios.delete(`${process.env.REACT_APP_SRV}/locations/${locationId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: `${orgId}`,
                },
            });
            toast.success('Location deleted successfully');
            await fetchLocations(); // Refetch locations after deletion
        } catch (error) {
            console.error('Error deleting location:', error);
            toast.error('Failed to delete location');
        }
    };

    const handleEditLocation = (location, org) => {
        setSelectedLocation(location); // Set the location to be edited
        setSelectedOrg(org);
        setNewLocation({               // Populate newLocation with selectedLocation details
            name: location.name,
            contact: location.contact,
            terminals: location.terminals,
        });
        setLocationDialogOpen(true);    // Open the dialog
    };

    const generateQRCodes = async (location) => {
        try {
            const { _id: placeId, terminals } = location;
            const baseUrl = `${process.env.REACT_APP_FRONT}/POS`;

            const qrCodes = [];
            for (let i = 1; i <= terminals; i++) {
                const qrUrl = `${baseUrl}?p=${placeId}&t=${i}`;
                const qrCode = await QRCode.toDataURL(qrUrl);
                qrCodes.push(qrCode);
            }

            exportToPDF(qrCodes, location.name);
            toast.success('QR Codes generated successfully');
        } catch (error) {
            console.error("Error generating QR codes:", error);
            toast.error('Failed to generate QR codes');
        }
    };

    // Export QR codes to PDF
    const exportToPDF = (qrCodes, locationName) => {
        const pdf = new jsPDF();
        qrCodes.forEach((qrCode, index) => {
            if (index > 0) {
                pdf.addPage();
            }
            pdf.text(`Location: ${locationName} - terminal# ${index + 1}`, 10, 10);
            pdf.addImage(qrCode, 'PNG', 10, 20, 50, 50);
        });

        pdf.save(`${locationName}_QR_Codes.pdf`);
    };


    return (
        <div>
            <ToastContainer position="bottom-right" />
            <div className="mb-4 flex justify-between items-center">
                <input
                    type="text"
                    placeholder="Search Organizations..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className="px-4 py-2 bg-gray-800 rounded text-white"
                />
                <button onClick={() => openOrgDialog()} className="bg-green-600 px-4 py-2 rounded hover:bg-green-700 flex items-center">
                    <FaPlus className="mr-2" /> Add Organization
                </button>
            </div>
            <ul>
                {filteredOrgs.map((org) => (
                    <li key={org._id} className="bg-gray-800 p-4 mb-2 rounded cursor-pointer" onClick={() => toggleExpand(org._id)}>
                        <div className="flex justify-between items-center">
                            <h3 className="text-xl">{org.orgName}</h3>
                            <div className="flex items-center">
                                <FaPlus className="text-green-600 cursor-pointer mr-2" onClick={() => handleAddLocation(org)} /> {/* Updated Icon */}
                                <FaEdit className="text-blue-600 cursor-pointer mr-2" onClick={() => openOrgDialog(org)} />
                                <FaTrash className="text-red-600 cursor-pointer" onClick={() => handleDeleteOrg(org._id)} />
                            </div>
                        </div>
                        {expandedOrgs[org._id] && (
                            <ul className="mt-2 ml-6">
                                {getLocationsForOrg(org._id).length > 0 ? (
                                    getLocationsForOrg(org._id).map((location) => (
                                        <li key={location._id} className="bg-gray-700 p-2 mb-2 rounded flex justify-between items-center">
                                            <div>
                                                <h4>{location.name}</h4>
                                                <p className="text-gray-400">Contact: {location.contact}</p>
                                                <p className="text-gray-400">Terminals: {location.terminals}</p>
                                            </div>
                                            <BiQr
                                onClick={() => generateQRCodes(location)}
                                className="ml-4 text-5xl text-black hover:text-white cursor-pointer"
                            />
                                            <div className="flex">
                                                <FaEdit
                                                    className="text-blue-500 cursor-pointer mr-2"
                                                    onClick={() => handleEditLocation(location, org)}
                                                />

                                                <FaTrash
                                                    className="text-red-500 cursor-pointer"
                                                    onClick={() => handleDeleteLocation(location._id, org._id)}
                                                />
                                            </div>
                                        </li>
                                    ))
                                ) : (
                                    <p className="text-gray-400">No locations found for this organization.</p>
                                )}
                            </ul>
                        )}
                    </li>
                ))}
            </ul>


            {/* Add/Edit Organization Dialog */}
            {orgDialogOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
                    <div className="bg-gray-800 p-6 rounded-lg">
                        <h3 className="text-xl mb-4">{selectedOrg._id ? 'Edit Organization' : 'Add New Organization'}</h3>
                        <input
                            type="text"
                            placeholder="Organization Name"
                            value={selectedOrg.orgName}
                            onChange={(e) => setSelectedOrg({ ...selectedOrg, orgName: e.target.value })}
                            className="block w-full mb-2 p-2 bg-gray-700 text-white rounded"
                        />
                        <input
                            type="text"
                            placeholder="Organization Number (OrgNr)"
                            value={selectedOrg.orgNr}
                            onChange={(e) => setSelectedOrg({ ...selectedOrg, orgNr: e.target.value })}
                            className="block w-full mb-2 p-2 bg-gray-700 text-white rounded"
                        />
                        <input
                            type="text"
                            placeholder="Valute"
                            value={selectedOrg.valute}
                            onChange={(e) => setSelectedOrg({ ...selectedOrg, valute: e.target.value })}
                            className="block w-full mb-4 p-2 bg-gray-700 text-white rounded"
                        />
                        <div className="flex justify-end">
                            <button onClick={() => setOrgDialogOpen(false)} className="bg-red-600 px-4 py-2 rounded hover:bg-red-700 mr-2">Cancel</button>
                            <button onClick={handleCreateOrUpdateOrg} className="bg-green-600 px-4 py-2 rounded hover:bg-green-700">
                                {selectedOrg._id ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Location Dialog */}
            {locationDialogOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
                    <div className="bg-gray-800 p-6 rounded-lg">
                        <h3 className="text-xl mb-4">
                            {selectedLocation?._id ? 'Edit Location' : 'Add New Location'}
                        </h3>
                        <input
                            type="text"
                            placeholder="Name"
                            value={newLocation.name}
                            onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                            className="block w-full mb-2 p-2 bg-gray-700 text-white rounded"
                        />
                        <input
                            type="text"
                            placeholder="Contact"
                            value={newLocation.contact}
                            onChange={(e) => setNewLocation({ ...newLocation, contact: e.target.value })}
                            className="block w-full mb-2 p-2 bg-gray-700 text-white rounded"
                        />
                        <label className="text-white mb-2 block">Terminals</label>
                        <input
                            type="number"
                            placeholder="Enter number of terminals"
                            value={newLocation.terminals}
                            onChange={(e) => setNewLocation({ ...newLocation, terminals: e.target.value })}
                            className="block w-full mb-4 p-2 bg-gray-700 text-white rounded"
                        />
                        <div className="flex justify-end">
                            <button onClick={resetLocationDialog} className="bg-red-600 px-4 py-2 rounded hover:bg-red-700 mr-2">
                                Cancel
                            </button>
                            <button onClick={handleSaveLocation} className="bg-green-600 px-4 py-2 rounded hover:bg-green-700">
                                {selectedLocation?._id ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default OrgManager;
